/**
 * WineLandingPage — high-intent SEO landing at `/wine` (national) and
 * `/wine/:city` (city-scoped). Wine-country aesthetic: warm burgundy/
 * gold gradient hero, vineyard imagery, sommelier-style copy.
 *
 * National mode (`/wine`) renders one rail per city sorted by upcoming
 * wine-event count. City mode (`/wine/:city`) renders a single grid +
 * deeper SEO copy ("wine events in {city}", "the {city} wine scene…").
 *
 * Both modes ship full JSON-LD: `CollectionPage` + breadcrumb + per-
 * city `ItemList` of `FoodEvent`. The page captures "wine events {city}"
 * organic search and feeds Google's Events carousel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wine, MapPin, Calendar, ChevronRight, Loader2, Grape, Sparkles, ArrowRight,
  Bell, Users, Mail, Phone, Building2, Megaphone, Repeat, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import SEO from '../components/SEO';
import { breadcrumbLD, eventsItemListLD, collectionPageLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';

// Only show "X people following" once the page has real social proof.
// Below this we hide the stat entirely so a fresh page never reads "0".
const FOLLOWER_PROOF_THRESHOLD = 35;

// Cities we actively curate. National page uses the API order (sorted by
// event count) but falls back to this list to render empty rails so the
// page never looks bare on a slow weekend.
const CURATED_CITIES = [
  { slug: 'denver',      name: 'Denver',      state: 'CO' },
  { slug: 'los-angeles', name: 'Los Angeles', state: 'CA' },
  { slug: 'las-vegas',   name: 'Las Vegas',   state: 'NV' },
  { slug: 'charlotte',   name: 'Charlotte',   state: 'NC' },
];

const capitalize = (s = '') =>
  String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

export default function CategoryLandingPage({ config }) {
  const { city } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCityMode = Boolean(city);
  const cityName = useMemo(() => (city ? capitalize(city) : ''), [city]);
  // Destructure the category config — kept as a single prop so per-
  // category landing pages stay 5-line wrappers.
  const {
    slug,          // 'wine', 'comedy', 'brunch', 'holiday'
    label,         // 'Wine', 'Comedy', 'Brunch', 'Holiday'
    plural,        // 'Wine Events', 'Comedy Nights', …
    accent,        // { primary: 'rose', secondary: 'amber' }
    heroEyebrow,   // 'The Eventism Wine Hub'
    heroImage,     // URL
    heroTagline,   // 'Wine, but worth showing up for'
    heroSubtitle,  // city / national subtitle copy
    badges,        // [{ icon, label }, ...]
    seoTitle: seoTitleFn,
    seoDescription: seoDescFn,
    seoKeywords: seoKeywordsFn,
    seoCopy,       // { city: <react node>, national: <react node> }
    organizerNoun, // 'wine event', 'comedy show', …
  } = config;

  const [loading, setLoading] = useState(true);
  const [cityEvents, setCityEvents] = useState([]);     // city mode
  const [cityRails, setCityRails] = useState([]);       // national mode

  // Category-page extras: follower count, popular venues, brands, series.
  // Fetched only in city mode (national mode shows aggregated rails so
  // venue/brand attribution by city is the right surface). Each is its
  // own state slice so a slow side fetch never blocks the main page paint.
  const [followerCount, setFollowerCount] = useState(0);
  const [popularVenues, setPopularVenues] = useState([]);
  const [brands, setBrands] = useState([]);
  const [series, setSeries] = useState([]);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const showFollowerProof = followerCount >= FOLLOWER_PROOF_THRESHOLD;

  // Surface a toast whenever the user lands back here from a confirm or
  // unsubscribe email link — and strip the param so a refresh doesn't
  // re-fire the toast.
  useEffect(() => {
    if (searchParams.get('confirmed') === '1') {
      toast.success(`You're confirmed — you'll hear from us when new ${slug} events drop.`);
      const next = new URLSearchParams(searchParams);
      next.delete('confirmed');
      setSearchParams(next, { replace: true });
    } else if (searchParams.get('unsubscribed') === '1') {
      toast('Unsubscribed. Come back any time.', { icon: '👋' });
      const next = new URLSearchParams(searchParams);
      next.delete('unsubscribed');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, slug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = isCityMode
      ? `${API}/categories/${slug}/events?city=${encodeURIComponent(city)}&limit=24`
      : `${API}/categories/${slug}/events?limit=12`;
    axios.get(url)
      .then((r) => {
        if (cancelled) return;
        if (isCityMode) {
          setCityEvents(r.data?.events || []);
        } else {
          const apiByCity = new Map(
            (r.data?.cities || []).map((c) => [c.city, c]),
          );
          const merged = CURATED_CITIES.map((c) => ({
            city: c.slug,
            name: c.name,
            state: c.state,
            events: apiByCity.get(c.slug)?.events || [],
            count: apiByCity.get(c.slug)?.count || 0,
          }));
          merged.sort((a, b) => {
            if ((b.count > 0) !== (a.count > 0)) return (b.count > 0) ? 1 : -1;
            return b.count - a.count;
          });
          setCityRails(merged);
        }
      })
      .catch(() => { if (!cancelled) { setCityEvents([]); setCityRails([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [city, isCityMode, slug]);

  // Side fetches — kicked off independently of the events fetch.
  useEffect(() => {
    let cancelled = false;
    const params = isCityMode ? { city } : {};
    const refreshCount = () =>
      axios.get(`${API}/categories/${slug}/follower-count`, { params })
        .then((r) => { if (!cancelled) setFollowerCount(r.data?.count || 0); })
        .catch(() => {});
    refreshCount();

    if (isCityMode) {
      axios.get(`${API}/categories/${slug}/popular-venues`, { params: { city, limit: 12 } })
        .then((r) => { if (!cancelled) setPopularVenues(r.data?.venues || []); })
        .catch(() => {});
      axios.get(`${API}/categories/${slug}/brands`, { params: { city, limit: 12 } })
        .then((r) => { if (!cancelled) setBrands(r.data?.brands || []); })
        .catch(() => {});
      axios.get(`${API}/categories/${slug}/series`, { params: { city, limit: 12 } })
        .then((r) => { if (!cancelled) setSeries(r.data?.series || []); })
        .catch(() => {});
    } else {
      setPopularVenues([]); setBrands([]); setSeries([]);
    }
    return () => { cancelled = true; };
  }, [city, isCityMode, slug]);

  // ---------------------------------------------------------------------
  // SEO — fully driven by the config helpers so per-category copy lives
  // in /configs/categoryLandings.js, not here.
  // ---------------------------------------------------------------------
  const canonicalUrl = `${SITE_URL}${isCityMode ? `/${slug}/${city}` : `/${slug}`}`;
  const seoTitle = seoTitleFn({ isCityMode, cityName });
  const seoDescription = seoDescFn({ isCityMode, cityName, eventCount: cityEvents.length });
  const seoKeywords = seoKeywordsFn({ isCityMode, cityName });

  const itemsLD = useMemo(() => {
    if (isCityMode) {
      return cityEvents.length > 0
        ? [eventsItemListLD(cityEvents, `${plural} in ${cityName}`, `/${slug}/${city}`)]
        : [];
    }
    return cityRails
      .filter((r) => r.events.length > 0)
      .map((r) => eventsItemListLD(r.events, `${plural} in ${r.name}`, `/${slug}/${r.city}`));
  }, [isCityMode, cityEvents, cityRails, city, cityName, slug, plural]);

  const structuredData = [
    collectionPageLD(seoTitle, seoDescription),
    breadcrumbLD([
      { name: 'Home', url: '/' },
      { name: plural, url: `/${slug}` },
      ...(isCityMode ? [{ name: cityName, url: `/${slug}/${city}` }] : []),
    ]),
    ...itemsLD,
  ].filter(Boolean);

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-background" data-testid="wine-landing">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        url={canonicalUrl}
        keywords={seoKeywords}
        structuredData={structuredData}
      />

      {/* HERO */}
      <section className="relative pt-24 pb-12 overflow-hidden" data-testid="category-hero">
        <div className="absolute inset-0 -z-10">
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            loading="eager"
            width={1600}
            height={900}
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className={`absolute inset-0 ${accent.heroOverlay}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top row: breadcrumbs + Subscribe button (top-right) */}
          <div className="flex items-center justify-between mb-6 gap-3">
            <nav className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="category-breadcrumbs">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to={`/${slug}`} className="hover:text-foreground">{plural}</Link>
              {isCityMode && (
                <>
                  <ChevronRight className="h-3.5 w-3.5" />
                  <span className="text-foreground">{cityName}</span>
                </>
              )}
            </nav>
            <Button
              size="sm"
              onClick={() => setSubscribeOpen(true)}
              className={`${accent.btnGradient} text-white hover:opacity-90 shadow-lg ${accent.btnShadow}`}
              data-testid="subscribe-open-btn"
            >
              <Bell className="h-3.5 w-3.5 mr-1.5" /> Follow
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-5">
              <p className={`text-[11px] uppercase tracking-[0.3em] font-bold ${accent.eyebrowColor} flex items-center gap-2`} data-testid="category-hero-eyebrow">
                <config.Icon className="h-4 w-4" />
                {isCityMode ? `${cityName} ${label} Scene` : heroEyebrow}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] leading-[1.05]" data-testid="category-h1">
                {isCityMode ? (
                  <>
                    {plural} in{' '}
                    <span className={`bg-gradient-to-r ${accent.titleGradient} bg-clip-text text-transparent`}>
                      {cityName}
                    </span>
                    .
                  </>
                ) : (
                  <>
                    {heroTagline.lead}{' '}
                    <span className={`bg-gradient-to-r ${accent.titleGradient} bg-clip-text text-transparent`}>
                      {heroTagline.highlight}
                    </span>
                    {heroTagline.trail}
                  </>
                )}
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                {isCityMode ? heroSubtitle.city({ cityName }) : heroSubtitle.national}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                {badges.map((b, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className={b.className || `${accent.badgeBorder} ${accent.badgeText} ${accent.badgeBg}`}
                  >
                    <b.icon className="h-3 w-3 mr-1.5" /> {b.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* City picker on national mode; otherwise stats stack */}
            {!isCityMode ? (
              <Card className={`bg-card/60 backdrop-blur ${accent.cardBorder}`} data-testid="category-city-picker">
                <CardContent className="p-5 space-y-3">
                  <p className={`text-xs uppercase tracking-wider ${accent.eyebrowColor} font-bold`}>Pick your city</p>
                  <div className="grid grid-cols-2 gap-2">
                    {CURATED_CITIES.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/${slug}/${c.slug}`}
                        className={`group flex items-center justify-between p-3 rounded-lg border border-border ${accent.hoverBorder} ${accent.hoverBg} transition-all`}
                        data-testid={`category-city-link-${c.slug}`}
                      >
                        <span className="font-semibold text-sm">{c.name}<span className="text-muted-foreground font-normal">, {c.state}</span></span>
                        <ArrowRight className={`h-4 w-4 text-muted-foreground group-hover:${accent.linkText} group-hover:translate-x-0.5 transition-all`} />
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className={`grid ${showFollowerProof ? 'grid-cols-3' : 'grid-cols-2'} gap-3`} data-testid="category-city-stats">
                <Stat
                  label="Upcoming events"
                  value={cityEvents.length}
                  icon={<Calendar className={`h-4 w-4 ${accent.statIconA}`} />}
                />
                {showFollowerProof && (
                  <Stat
                    label="People following"
                    value={followerCount.toLocaleString()}
                    icon={<Users className={`h-4 w-4 ${accent.statIconB}`} />}
                    testId="category-follower-count"
                  />
                )}
                <Stat
                  label="Curated by Eventism"
                  value={'24/7'}
                  icon={<Sparkles className={`h-4 w-4 ${accent.statIconB}`} />}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Subscribe dialog — opened by the top-right button */}
      <Dialog open={subscribeOpen} onOpenChange={setSubscribeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="subscribe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Bell className={`h-5 w-5 ${accent.linkText}`} />
              Never miss a {isCityMode ? `${cityName} ` : ''}{slug} drop
            </DialogTitle>
            <DialogDescription>
              We&apos;ll notify you when new {plural.toLowerCase()} are added.
              {showFollowerProof
                ? ` Join ${followerCount.toLocaleString()} other ${isCityMode ? cityName + ' ' : ''}${label.toLowerCase()} fans.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <FollowForm
            category={slug}
            city={isCityMode ? city : null}
            onClose={() => setSubscribeOpen(false)}
            accent={accent}
          />
        </DialogContent>
      </Dialog>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="py-20 flex justify-center" data-testid="category-loading">
            <Loader2 className={`h-8 w-8 animate-spin ${accent.linkText}`} />
          </div>
        ) : isCityMode ? (
          /* City-scoped page: single grid */
          cityEvents.length === 0 ? (
            <Card className="border-dashed text-center py-12" data-testid="category-city-empty">
              <CardContent>
                <config.Icon className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  No {plural.toLowerCase()} scheduled in {cityName} just yet — check back soon, or browse another city.
                </p>
                <Button asChild size="sm">
                  <Link to={`/${slug}`}>See all {plural.toLowerCase()}</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <section data-testid="category-city-grid-section">
              <div className="flex items-end justify-between mb-5">
                <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
                  Upcoming in {cityName}
                </h2>
                <Link to={`/events/${city}`} className={`text-sm ${accent.linkText} hover:underline`}>
                  All {cityName} events <ChevronRight className="h-3.5 w-3.5 inline" />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="category-city-grid">
                {cityEvents.map((e) => (
                  <EventCard key={e.event_id} event={e} config={config} />
                ))}
              </div>
            </section>
          )
        ) : (
          /* National page: one rail per city */
          <div className="space-y-12" data-testid="category-national-rails">
            {cityRails.map((rail) => (
              <CityRail key={rail.city} rail={rail} config={config} slug={slug} />
            ))}
          </div>
        )}

        {/* CITY-MODE EXTRAS: popular venues, brands, series */}
        {isCityMode && (
          <>
            {popularVenues.length > 0 && (
              <CategoryVenuesSection cityName={cityName} city={city} venues={popularVenues} config={config} />
            )}
            {brands.length > 0 && (
              <CategoryBrandsSection cityName={cityName} brands={brands} config={config} />
            )}
            {series.length > 0 && (
              <CategorySeriesSection cityName={cityName} city={city} series={series} config={config} />
            )}
          </>
        )}

        {/* SEO long-form copy — driven by config so each category gets
            its own voice (sommelier / comedy / brunch / holiday). */}
        <section className="mt-16 max-w-3xl mx-auto" data-testid="category-seo-copy">
          <h2 className="text-xl sm:text-2xl font-bold font-['Space_Grotesk'] mb-4">
            {isCityMode ? seoCopy.cityTitle({ cityName }) : seoCopy.nationalTitle}
          </h2>
          <div className="prose prose-sm prose-invert max-w-none text-muted-foreground leading-relaxed space-y-3">
            {isCityMode ? seoCopy.city({ cityName, city, slug, accent }) : seoCopy.national({ slug, accent })}
          </div>
        </section>
      </div>

      {/* ORGANIZER CTA — bottom-of-page banner pitch */}
      <OrganizerCTABanner cityName={isCityMode ? cityName : null} category={slug} organizerNoun={organizerNoun} accent={accent} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------
function Stat({ label, value, icon, testId }) {
  return (
    <div className="rounded-lg border border-border bg-card/40 backdrop-blur px-4 py-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {icon}{label}
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function CityRail({ rail, config, slug }) {
  return (
    <section data-testid={`category-city-rail-${rail.city}`}>
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className={`text-[10px] uppercase tracking-[0.25em] font-bold ${config.accent.linkText} flex items-center gap-1.5`}>
            <MapPin className="h-3 w-3" /> {rail.name}, {rail.state}
          </p>
          <h2 className="text-xl sm:text-2xl font-bold font-['Space_Grotesk'] mt-1">
            {rail.count > 0
              ? `${rail.count} upcoming ${config.label.toLowerCase()} ${rail.count === 1 ? 'event' : 'events'}`
              : `New ${config.label.toLowerCase()} events coming to ${rail.name}`}
          </h2>
        </div>
        <Link
          to={`/${slug}/${rail.city}`}
          className={`text-sm ${config.accent.linkText} hover:underline whitespace-nowrap`}
          data-testid={`category-rail-more-${rail.city}`}
        >
          See all <ChevronRight className="h-3.5 w-3.5 inline" />
        </Link>
      </div>
      {rail.events.length === 0 ? (
        <Card className="border-dashed py-8 text-center text-xs text-muted-foreground">
          <CardContent>
            Nothing scheduled yet — bookmark this page or follow us for {rail.name} drops.
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rail.events.slice(0, 6).map((e) => (
            <EventCard key={e.event_id} event={e} config={config} compact />
          ))}
        </div>
      )}
    </section>
  );
}

function EventCard({ event: e, config, compact = false }) {
  const { accent, Icon } = config;
  return (
    <Link
      to={e.url}
      className={`group rounded-xl overflow-hidden border border-border ${accent.hoverBorder} transition-colors bg-card flex flex-col`}
      data-testid={`category-event-card-${e.slug}`}
    >
      <div className={`relative ${compact ? 'aspect-[16/10]' : 'aspect-[16/9]'} ${accent.cardImageBg} overflow-hidden`}>
        {e.image_url ? (
          <img
            src={e.image_url}
            alt={e.name}
            loading="lazy"
            width={560}
            height={350}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className={`h-10 w-10 ${accent.fallbackIcon}`} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        {e.is_featured && (
          <Badge className="absolute top-2.5 left-2.5 bg-amber-500 text-black border-0 text-[10px]">
            <Sparkles className="h-3 w-3 mr-1" /> Featured
          </Badge>
        )}
      </div>
      <div className="p-3 space-y-1.5 flex-1">
        <h3 className="font-semibold text-sm leading-snug line-clamp-2 group-hover:text-rose-300 transition-colors">
          {e.name}
        </h3>
        {e.tagline && !compact && (
          <p className="text-xs text-muted-foreground line-clamp-2">{e.tagline}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatShortDate(e.event_date)}
          </span>
          {e.venue_name && <span className="line-clamp-1">{e.venue_name}</span>}
        </div>
        {e.ticket_price_min != null && e.ticket_price_min !== '' && (
          <p className={`text-xs ${accent.priceText} font-bold pt-1`}>From ${e.ticket_price_min}</p>
        )}
      </div>
    </Link>
  );
}


// ======================================================================
// FollowForm — double-opt-in signup form (rendered inside Subscribe dialog)
// ======================================================================
function FollowForm({ category, city, onClose, accent }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !/.+@.+\..+/.test(email)) {
      toast.error('Enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/categories/${category}/follow`, {
        email: email.trim(),
        phone: phone.trim() || undefined,
        name:  name.trim()  || undefined,
        city:  city || undefined,
        frequency,
      });
      setSubmitted(true);
      toast.success('Check your email to confirm — and you\'re in.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not subscribe. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-6 text-center space-y-2" data-testid="follow-submitted">
        <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-400" />
        <h3 className="font-bold text-lg">Almost there — check your inbox</h3>
        <p className="text-sm text-muted-foreground">
          We sent <strong className="text-foreground">{email}</strong> a confirmation link.
          Click it and you&apos;re officially in.
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onClose} data-testid="follow-done-btn">
          Done
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="follow-form">
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1.5">
          <Mail className="h-3 w-3" /> Email *
        </Label>
        <Input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="follow-email-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1.5">
          <Phone className="h-3 w-3" /> Phone (for text alerts)
        </Label>
        <Input
          type="tel"
          placeholder="(555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="follow-phone-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">How often should we email you?</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="follow-frequency-group">
          {[
            { value: 'instant', label: 'Instant',  hint: 'each new event' },
            { value: 'weekly',  label: 'Weekly',   hint: 'Wed digest' },
            { value: 'monthly', label: 'Monthly',  hint: 'Sun digest' },
          ].map((f) => (
            <button
              type="button"
              key={f.value}
              onClick={() => setFrequency(f.value)}
              className={`text-left p-2.5 rounded-lg border transition-all ${
                frequency === f.value
                  ? (accent?.formActiveBorder || 'border-rose-400 bg-rose-500/10')
                  : `border-border ${accent?.formHoverBorder || 'hover:border-rose-400/40'}`
              }`}
              data-testid={`follow-frequency-${f.value}`}
            >
              <p className="text-sm font-semibold">{f.label}</p>
              <p className="text-[10px] text-muted-foreground">{f.hint}</p>
            </button>
          ))}
        </div>
      </div>
      <Button
        type="submit"
        disabled={submitting}
        className={`w-full ${accent?.btnGradient || 'bg-gradient-to-r from-rose-500 to-amber-500'} text-white hover:opacity-90`}
        data-testid="follow-submit-btn"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
        Follow
      </Button>
      <p className="text-[10px] text-muted-foreground text-center">
        Phone is optional — we&apos;ll text you when SMS launches. Email confirmation required to activate.
      </p>
    </form>
  );
}

// ======================================================================
// CategoryVenuesSection — popular wine venues in this city
// ======================================================================
function CategoryVenuesSection({ cityName, city, venues, config }) {
  const { accent, label } = config;
  return (
    <section className="mt-16" data-testid="category-venues-section">
      <div className="flex items-end justify-between mb-5 gap-3">
        <div>
          <p className={`text-[10px] uppercase tracking-[0.25em] font-bold ${accent.linkText} flex items-center gap-1.5`}>
            <Building2 className="h-3 w-3" /> Popular {label.toLowerCase()} venues
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] mt-1">
            Where {cityName} gathers
          </h2>
        </div>
        <Link to={`/${city}/venues`} className={`text-sm ${accent.linkText} hover:underline whitespace-nowrap`}>
          All {cityName} venues <ChevronRight className="h-3.5 w-3.5 inline" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {venues.map((v) => (
          <Link
            key={v.slug}
            to={v.url}
            className={`group rounded-xl overflow-hidden border border-border ${accent.hoverBorder} transition-colors bg-card flex flex-col`}
            data-testid={`category-venue-card-${v.slug}`}
          >
            <div className={`relative aspect-[16/10] ${accent.cardImageBg} overflow-hidden`}>
              {v.cover_photo ? (
                <img src={v.cover_photo} alt={v.name} loading="lazy" width={560} height={350}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Building2 className={`h-10 w-10 ${accent.fallbackIcon}`} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              {v.upcoming_events > 0 ? (
                <Badge className={`absolute top-2.5 right-2.5 ${accent.badgePrimary} border-0 text-[10px]`}>
                  {v.upcoming_events} upcoming
                </Badge>
              ) : v.total_events > 0 && (
                <Badge variant="outline" className={`absolute top-2.5 right-2.5 bg-black/60 ${accent.badgeBorder} text-[10px]`}>
                  Past host
                </Badge>
              )}
            </div>
            <div className="p-3 space-y-1">
              <h3 className={`font-semibold text-sm group-hover:${accent.linkText} transition-colors line-clamp-2`}>{v.name}</h3>
              {v.address && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1 line-clamp-1">
                  <MapPin className="h-3 w-3" />{v.address}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ======================================================================
// CategoryBrandsSection — brands hosting upcoming events in this category
// ======================================================================
function CategoryBrandsSection({ cityName, brands, config }) {
  const { accent, label } = config;
  return (
    <section className="mt-14" data-testid="category-brands-section">
      <div className="mb-5">
        <p className={`text-[10px] uppercase tracking-[0.25em] font-bold ${accent.eyebrowColor} flex items-center gap-1.5`}>
          <Sparkles className="h-3 w-3" /> Series & Clubs
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] mt-1">
          {label} Series & Clubs to follow in {cityName}
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {brands.map((b) => (
          <Link
            key={b.brand_id}
            to={b.url}
            className={`group rounded-xl overflow-hidden border border-border ${accent.hoverBorderAlt || accent.hoverBorder} transition-colors bg-card flex flex-col`}
            data-testid={`category-brand-card-${b.slug}`}
          >
            <div
              className="relative aspect-[16/9] overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${b.accent_color}33, transparent)` }}
            >
              {b.hero_image_url ? (
                <img src={b.hero_image_url} alt={b.name} loading="lazy" width={560} height={315}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              ) : (
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${b.accent_color}, #1a0610)` }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              {b.logo_url && (
                <img src={b.logo_url} alt="" loading="lazy"
                  className="absolute bottom-3 left-3 h-10 w-10 rounded-md border-2 border-white/40 object-cover" />
              )}
              <Badge className={`absolute top-2.5 right-2.5 ${accent.badgeSecondary || accent.badgePrimary} border-0 text-[10px]`}>
                {b.upcoming_events} upcoming
              </Badge>
            </div>
            <div className="p-3 space-y-1.5">
              {b.kind && (
                <p className={`text-[9px] uppercase tracking-[0.18em] font-bold ${accent.eyebrowColor}`}>
                  {b.kind === 'club' ? 'Membership Club' : b.kind === 'program' ? 'Program' : 'Recurring Series'}
                </p>
              )}
              <h3 className={`font-semibold text-sm group-hover:${accent.eyebrowColor} transition-colors line-clamp-1`}>{b.name}</h3>
              {b.tagline && <p className="text-[11px] text-muted-foreground line-clamp-2">{b.tagline}</p>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ======================================================================
// CategorySeriesSection — recurring series with live events in this city
// ======================================================================
function CategorySeriesSection({ cityName, city, series, config }) {
  const { accent, label } = config;
  return (
    <section className="mt-14" data-testid="category-series-section">
      <div className="mb-5">
        <p className={`text-[10px] uppercase tracking-[0.25em] font-bold ${accent.linkText} flex items-center gap-1.5`}>
          <Repeat className="h-3 w-3" /> Recurring series
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] mt-1">
          {label} series running in {cityName}
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {series.map((s) => {
          const target = s.venue_slug ? `/${city}/venues/${s.venue_slug}` : `/events/${city}`;
          return (
            <Link
              key={s.series_id}
              to={target}
              className={`group rounded-xl overflow-hidden border border-border ${accent.hoverBorder} transition-colors bg-card flex flex-col`}
              data-testid={`category-series-card-${s.series_id}`}
            >
              <div className={`relative aspect-[16/9] ${accent.cardImageBg} overflow-hidden`}>
                {s.image_url ? (
                  <img src={s.image_url} alt={s.series_name} loading="lazy" width={560} height={315}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Repeat className={`h-10 w-10 ${accent.fallbackIcon}`} />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <Badge className={`absolute top-2.5 right-2.5 ${accent.badgePrimary} border-0 text-[10px]`}>
                  {s.event_count} dates
                </Badge>
              </div>
              <div className="p-3 space-y-1">
                <h3 className={`font-semibold text-sm group-hover:${accent.linkText} transition-colors line-clamp-1`}>{s.series_name}</h3>
                {s.venue_name && (
                  <p className="text-[11px] text-muted-foreground line-clamp-1">at {s.venue_name}</p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ======================================================================
// OrganizerCTABanner — bottom-of-page pitch for organizers
// ======================================================================
function OrganizerCTABanner({ cityName, category, organizerNoun, accent }) {
  const noun = organizerNoun || `${category || 'event'} event`;
  return (
    <section
      className={`relative overflow-hidden border-t border-border ${accent?.ctaBg || 'bg-gradient-to-br from-amber-950/30 via-background to-rose-950/30'}`}
      data-testid="organizer-cta-banner"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-5 gap-8 items-center">
        <div className="lg:col-span-3 space-y-3">
          <p className={`text-[10px] uppercase tracking-[0.25em] font-bold ${accent?.eyebrowColor || 'text-amber-300'} flex items-center gap-1.5`}>
            <Megaphone className="h-3 w-3" /> Hosting a {noun}?
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Space_Grotesk'] leading-tight">
            List it on Eventism. Get in front of {cityName ? `${cityName}'s` : 'every'} {category || 'event'} crowd.
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
            Free to list. Built-in ticketing with Stripe. SEO-optimised event pages, automated post-event reports,
            and a CRM that re-targets the right guests for your next event.
          </p>
        </div>
        <div className="lg:col-span-2 flex flex-col sm:flex-row gap-3" data-testid="organizer-cta-buttons">
          <Button asChild size="lg" className={`flex-1 ${accent?.btnGradient || 'bg-gradient-to-r from-amber-500 to-rose-500'} text-white hover:opacity-90`}>
            <Link to="/admin/event-manager" data-testid="organizer-cta-list">
              List your event <ArrowRight className="h-4 w-4 ml-1.5" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className={`flex-1 ${accent?.btnOutline || 'border-rose-400/40 hover:bg-rose-500/10'}`}>
            <Link to="/contact" data-testid="organizer-cta-contact">
              Talk to us
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
